import React, { useEffect, useState } from 'react';
import { Activity, GitBranch, Users } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { toast } from 'sonner';

import DataTable from '../components/DataTable';
import EmptyState from '../components/EmptyState';
import LoadingSkeleton from '../components/LoadingSkeleton';
import StatCard from '../components/StatCard';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import api, { analyticsApi } from '../lib/api';

const RANGE_OPTIONS = [
  { key: '7d', label: '7d', days: 7 },
  { key: '30d', label: '30d', days: 30 },
  { key: '90d', label: '90d', days: 90 },
];

const AUTHOR_COLORS = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ec4899', '#14b8a6'];

const tooltipStyle = {
  backgroundColor: '#1e293b',
  border: '1px solid #334155',
  borderRadius: '12px',
  color: '#e2e8f0',
};

function getDateWindow(rangeKey) {
  const option = RANGE_OPTIONS.find((range) => range.key === rangeKey) || RANGE_OPTIONS[1];
  const end = new Date();
  end.setHours(23, 59, 59, 999);

  const start = new Date(end);
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - (option.days - 1));

  return {
    days: option.days,
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10),
  };
}

function formatDayLabel(value) {
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
  }).format(new Date(value));
}

function formatTimestamp(value) {
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function parsePayload(payload) {
  if (!payload) return {};
  if (typeof payload === 'object') return payload;

  try {
    return JSON.parse(payload);
  } catch (error) {
    try {
      return JSON.parse(
        String(payload)
          .replace(/\bNone\b/g, 'null')
          .replace(/\bTrue\b/g, 'true')
          .replace(/\bFalse\b/g, 'false')
          .replace(/'/g, '"')
      );
    } catch (nestedError) {
      return {};
    }
  }
}

function truncateMessage(value = '', length = 60) {
  if (value.length <= length) return value;
  return `${value.slice(0, length - 1)}…`;
}

function buildCommitSeries(commitRows = [], days = 30) {
  const series = Array.from({ length: days }).map((_, index) => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() - (days - index - 1));
    const key = date.toISOString().slice(0, 10);
    return {
      date: key,
      label: formatDayLabel(key),
    };
  });

  const topAuthors = Object.entries(
    commitRows.reduce((accumulator, row) => {
      const payload = parsePayload(row.payload_json);
      const author = payload.author || row.employee_name || 'Unknown';
      accumulator[author] = (accumulator[author] || 0) + 1;
      return accumulator;
    }, {})
  )
    .sort((first, second) => second[1] - first[1])
    .slice(0, 5)
    .map(([author]) => author);

  series.forEach((point) => {
    topAuthors.forEach((author) => {
      point[author] = 0;
    });
  });

  const pointMap = series.reduce((accumulator, point) => {
    accumulator[point.date] = point;
    return accumulator;
  }, {});

  commitRows.forEach((row) => {
    const dateKey = String(row.ts || '').slice(0, 10);
    if (!pointMap[dateKey]) return;
    const payload = parsePayload(row.payload_json);
    const author = payload.author || row.employee_name || 'Unknown';
    if (!topAuthors.includes(author)) return;
    pointMap[dateKey][author] += 1;
  });

  return { data: series, authors: topAuthors };
}

function buildContributorBreakdown(commitRows = []) {
  const authorMap = {};

  commitRows.forEach((row) => {
    const payload = parsePayload(row.payload_json);
    const author = payload.author || row.employee_name || 'Unknown';
    authorMap[author] = (authorMap[author] || 0) + 1;
  });

  return Object.entries(authorMap)
    .map(([name, value]) => ({ name, value }))
    .sort((first, second) => second.value - first.value);
}

export default function GitAnalytics() {
  const [range, setRange] = useState('30d');
  const [repo, setRepo] = useState('all');
  const [loading, setLoading] = useState(true);
  const [tablePage, setTablePage] = useState(1);
  const [data, setData] = useState({
    summary: null,
    commits: [],
    allRows: [],
  });

  useEffect(() => {
    setTablePage(1);
  }, [range, repo]);

  useEffect(() => {
    const load = async () => {
      const { startDate, endDate } = getDateWindow(range);
      setLoading(true);

      const [summaryResult, exportResult] = await Promise.allSettled([
        analyticsApi.git({ start_date: startDate, end_date: endDate }),
        api.get('/export/git', {
          params: { date_from: startDate, date_to: endDate, page: 1, page_size: 1000 },
        }),
      ]);

      if (summaryResult.status === 'rejected') {
        toast.error('Failed to load git analytics.');
      }

      if (exportResult.status === 'rejected') {
        toast.error('Detailed git events are unavailable for this account.');
      }

      const allRows = exportResult.status === 'fulfilled' ? exportResult.value.data?.items || [] : [];
      const commitRows = allRows.filter((row) => row.type === 'commit');

      setData({
        summary: summaryResult.status === 'fulfilled' ? summaryResult.value.data : null,
        commits: commitRows,
        allRows,
      });
      setLoading(false);
    };

    load();
  }, [range]);

  const repositories = [...new Set(data.commits.map((row) => row.repo).filter(Boolean))].sort();
  const filteredCommits =
    repo === 'all' ? data.commits : data.commits.filter((row) => row.repo === repo);
  const contributorBreakdown = buildContributorBreakdown(filteredCommits);
  const commitSeries = buildCommitSeries(filteredCommits, getDateWindow(range).days);
  const pageSize = 10;
  const recentCommits = [...filteredCommits]
    .sort((first, second) => new Date(second.ts).getTime() - new Date(first.ts).getTime());
  const paginatedCommits = recentCommits.slice((tablePage - 1) * pageSize, tablePage * pageSize);
  const activeContributors = new Set(contributorBreakdown.map((item) => item.name)).size;
  const totalCommits = filteredCommits.length || data.summary?.commits || 0;
  const avgCommitsPerDay = totalCommits / Math.max(getDateWindow(range).days, 1);

  return (
    <div className="min-h-screen space-y-6 bg-slate-900 text-white" data-testid="git-analytics-page">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <p className="text-sm uppercase tracking-[0.35em] text-slate-500">Engineering</p>
          <h1 className="mt-2 text-4xl font-semibold tracking-tight text-white">Git Analytics</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-400">
            Commit volume, contributor balance, and recent repository activity across the selected
            period.
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="inline-flex rounded-xl border border-slate-700 bg-slate-800/80 p-1">
            {RANGE_OPTIONS.map((option) => (
              <button
                key={option.key}
                type="button"
                className={`rounded-lg px-4 py-2 text-sm transition ${
                  range === option.key
                    ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/20'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
                onClick={() => setRange(option.key)}
              >
                {option.label}
              </button>
            ))}
          </div>

          <Select value={repo} onValueChange={setRepo}>
            <SelectTrigger className="w-[220px] border-slate-700 bg-slate-800 text-slate-200">
              <SelectValue placeholder="All repositories" />
            </SelectTrigger>
            <SelectContent className="border-slate-700 bg-slate-800 text-slate-200">
              <SelectItem value="all">All Repositories</SelectItem>
              {repositories.map((repository) => (
                <SelectItem key={repository} value={repository}>
                  {repository}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <>
          <LoadingSkeleton type="card" />
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <LoadingSkeleton type="chart" />
            <LoadingSkeleton type="chart" />
          </div>
          <LoadingSkeleton type="table" />
        </>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <StatCard
              title="Total Commits"
              value={totalCommits}
              change={avgCommitsPerDay * 10}
              changeLabel="delivery pace"
              icon={<GitBranch className="h-5 w-5" />}
              color="blue"
            />
            <StatCard
              title="Active Contributors"
              value={activeContributors}
              change={activeContributors ? (totalCommits / activeContributors) * 10 : 0}
              changeLabel="commits per contributor"
              icon={<Users className="h-5 w-5" />}
              color="green"
            />
            <StatCard
              title="Avg Commits/Day"
              value={avgCommitsPerDay.toFixed(1)}
              change={avgCommitsPerDay * 10}
              changeLabel="daily throughput"
              icon={<Activity className="h-5 w-5" />}
              color="purple"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <Card className="border-slate-700 bg-slate-800 shadow-lg">
              <CardHeader className="pb-0">
                <CardTitle className="text-white">Commit Activity</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {commitSeries.data.length && commitSeries.authors.length ? (
                  <div className="h-[340px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={commitSeries.data}>
                        <CartesianGrid stroke="#334155" strokeDasharray="3 3" />
                        <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                        <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} />
                        <Tooltip contentStyle={tooltipStyle} />
                        {commitSeries.authors.map((author, index) => (
                          <Bar
                            key={author}
                            dataKey={author}
                            stackId="commits"
                            fill={AUTHOR_COLORS[index % AUTHOR_COLORS.length]}
                          />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <EmptyState
                    icon={<GitBranch className="h-8 w-8" />}
                    title="No commit activity"
                    description="Commit exports are empty for the selected range."
                  />
                )}
              </CardContent>
            </Card>

            <Card className="border-slate-700 bg-slate-800 shadow-lg">
              <CardHeader className="pb-0">
                <CardTitle className="text-white">Contributor Breakdown</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {contributorBreakdown.length ? (
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr),220px] lg:items-center">
                    <div className="h-[340px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={contributorBreakdown}
                            dataKey="value"
                            nameKey="name"
                            innerRadius={72}
                            outerRadius={112}
                            paddingAngle={4}
                          >
                            {contributorBreakdown.map((item, index) => (
                              <Cell
                                key={item.name}
                                fill={AUTHOR_COLORS[index % AUTHOR_COLORS.length]}
                              />
                            ))}
                          </Pie>
                          <Tooltip contentStyle={tooltipStyle} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-3">
                      {contributorBreakdown.map((item, index) => (
                        <div key={item.name} className="rounded-xl border border-slate-700 bg-slate-900/70 p-3">
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                              <span
                                className="h-3 w-3 rounded-full"
                                style={{ backgroundColor: AUTHOR_COLORS[index % AUTHOR_COLORS.length] }}
                              />
                              <span className="text-sm text-slate-200">{item.name}</span>
                            </div>
                            <span className="text-sm text-slate-400">{item.value}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <EmptyState
                    icon={<Users className="h-8 w-8" />}
                    title="No contributors"
                    description="No commit payloads were available for contributor breakdown."
                  />
                )}
              </CardContent>
            </Card>
          </div>

          <Card className="border-slate-700 bg-slate-800 shadow-lg">
            <CardHeader className="pb-0">
              <CardTitle className="text-white">Recent Commits</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <DataTable
                columns={[
                  {
                    key: 'author',
                    label: 'Author',
                    render: (_, row) => parsePayload(row.payload_json).author || row.employee_name || 'Unknown',
                  },
                  { key: 'repo', label: 'Repository' },
                  {
                    key: 'message',
                    label: 'Message',
                    render: (_, row) => (
                      <span className="line-clamp-1 max-w-[420px] text-slate-200">
                        {truncateMessage(parsePayload(row.payload_json).message || 'No message')}
                      </span>
                    ),
                  },
                  {
                    key: 'filesChanged',
                    label: 'Files Changed',
                    render: (_, row) =>
                      parsePayload(row.payload_json).files_changed ||
                      parsePayload(row.payload_json).changed_files ||
                      'N/A',
                  },
                  {
                    key: 'ts',
                    label: 'Timestamp',
                    render: (value) => formatTimestamp(value),
                  },
                ]}
                data={paginatedCommits}
                loading={loading}
                emptyMessage="No commit records are available for the current filters."
                pagination={{
                  page: tablePage,
                  pageSize,
                  total: recentCommits.length,
                  onPageChange: setTablePage,
                }}
              />
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
